import { forwardRef, type HTMLAttributes } from "react";
import { StrongText, Text } from "./";
import Image, { type StaticImageData } from "next/image";
import Link, { type LinkProps } from "next/link";
import { ExternalLink, Image as ImageIcon } from "lucide-react";

import { cn } from "@/lib/utils";

const CardPrimitive = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("border bg-card text-card-foreground shadow-sm", className)}
      {...props}
    />
  )
);
CardPrimitive.displayName = "CardPrimitive";

interface LinkCardProps extends LinkProps {
  className?: string;
  thumbSrc?: string | StaticImageData;
  target?: "_blank" | "_self";
  thumbAlt?: string;
  title?: string;
  description?: string;
}

export function LinkCard({
  className,
  thumbSrc,
  thumbAlt,
  title,
  target = "_self",
  description,
  ...linkProps
}: LinkCardProps) {
  return (
    <Link {...linkProps} target={target} className={className}>
      <CardPrimitive className="flex p-4 bg-muted relative">
        <div className="relative h-[100px] min-h-[100px] min-w-[100px] w-[100px] flex justify-center content-center items-center me-4">
          {!thumbSrc && (
            <ImageIcon className="w-[50%] h-[50%] text-muted-foreground" />
          )}
          {thumbSrc && (
            <Image
              src={thumbSrc}
              alt={thumbAlt || ""}
              fill
              style={{ objectFit: "contain" }}
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            />
          )}
        </div>
        <div className="flex flex-col justify-start items-stretch gap-2">
          {title && <StrongText className="pe-8">{title}</StrongText>}
          {description && <Text>{description}</Text>}
        </div>
        <ExternalLink className="absolute text-foreground top-3 end-3" />
      </CardPrimitive>
    </Link>
  );
}
